import React, { useEffect } from 'react';
import { useLoaderData, useParams } from '@tanstack/react-router';
import { SubmissionLoaderResponse } from '../../utils/submission-route-loader';
import {
  isUnauthorizedError,
  isExpiredToken,
  isNotFoundError,
} from '../../utils/errors';
import ErrorPage from '../status-pages/error-page';
import { getSurveyModel } from '../survey-questions/get-survey-model';
import { displayCustomPage } from '../../utils/custom-page/display-custom-page';
import { TokenExpirationMessage } from '../token-expiration-message/token-expiration-message';
import Survey from '../survey/survey';
import { setDatadogUser } from '../../utils/datadog-user-tracking';

const SurveySubmissionWithToken: React.FC = () => {
  const {
    settings,
    submission,
    error,
  } = useLoaderData({ from: '/$accountSlug/$submissionId/$token/' }) as SubmissionLoaderResponse;

  const { submissionId, token } = useParams({ from: '/$accountSlug/$submissionId/$token/' });

  // Track user in Datadog when submission is loaded
  useEffect(() => {
    if (submission) {
      setDatadogUser(submission);
    }
  }, [ submission ]);

  // If there's no error, the loader will have redirected to the clean route
  // So we only need to handle error cases here
  if (isNotFoundError(error)) {
    return <ErrorPage
      errorType={error}
      link={`/${settings.urlSlug}/`}
           />;
  }

  // Handle token expiration - user can request a new token
  if (isExpiredToken(error)) {
    const surveyModel = getSurveyModel({
      settings,
      submission,
      readOnly: true,
      completedHtml: '',
      currentPage: 1,
    });

    displayCustomPage({
      survey: surveyModel,
      component: TokenExpirationMessage,
      getProps: () => ({
        publicId: submissionId,
        slug: settings.urlSlug,
        currentToken: token,
      }),
      withoutFrame: true,
    });

    // Remove all pages except custom page
    const customPage = surveyModel.getPageByName(TokenExpirationMessage.name);
    surveyModel.pages.slice().forEach((page) => {
      if (page !== customPage) {
        surveyModel.removePage(page);
      }
    });
    surveyModel.showProgressBar = 'off';
    surveyModel.showTOC = false;
    surveyModel.showNavigationButtons = false;

    return (
      <Survey
        surveyModel={surveyModel}
      />
    );
  }

  // Handle invalid/wrong token - show error page
  if (isUnauthorizedError(error)) {
    return <ErrorPage
      errorType={error}
      link={`/${settings.urlSlug}/`}
           />;
  }

  // If we reach here, there's no error and redirect should have happened
  // Return null as a fallback (shouldn't happen)
  return null;
};

export default SurveySubmissionWithToken;
